import { useState } from "react";
import { useT } from "../../i18n/hooks.ts";
import type { SubBasin } from "../../model/types.ts";

/** Never a valid stringified option value (those are always plain numbers) — distinguishes "the user explicitly picked 'unassigned'" from "" (nothing picked yet, button stays disabled). */
const UNASSIGN_TOKEN = "__unassign__";

/**
 * Multi-selection + one value to apply to all — same pattern as
 * Modelos.vb/AsignacionZonas.vb/AsigPDS.vb/AsigAcuif.vb in the original.
 * The selection is state controlled by the parent so it can be shared with
 * the map (see AssignmentMap.tsx): in the original, selecting on the map
 * checked the same boxes in this list, and vice versa — they're two ways of
 * touching the same state, not two separate mechanisms.
 *
 * The dropdown always offers "— unassigned —" first, same option (and same
 * i18n key) as EditSubBasinDialog.tsx's own FieldSelector uses for a single
 * sub-basin — bulk assignment couldn't clear a field before this, only set
 * it to a real zone/outlet/aquifer. `TUnassign` is whichever sentinel the
 * caller's own field actually uses for "nothing assigned" (0 for outlet/
 * aquifer, `null` for zone/snow zone) — inferred from `unassignValue`, so
 * `onAssign`'s signature matches each caller's real assignment function
 * without a cast.
 */
export function BulkAssigner<TUnassign extends number | null>({
  subBasins,
  valueLabel,
  options,
  buttonText,
  selection,
  onChangeSelection,
  unassignValue,
  onAssign,
}: {
  subBasins: SubBasin[];
  valueLabel: (c: SubBasin) => string;
  options: { value: number; label: string }[];
  buttonText: string;
  selection: Set<number>;
  onChangeSelection: (selection: Set<number>) => void;
  /** The value onAssign is called with when "— unassigned —" is chosen — e.g. 0 for outletCode/aquiferCode, null for zoneCode/snowZoneCode. */
  unassignValue: TUnassign;
  onAssign: (codes: Set<number>, value: number | TUnassign) => void;
}) {
  const [value, setValue] = useState("");
  const t = useT();

  const toggle = (code: number) => {
    const copy = new Set(selection);
    if (copy.has(code)) copy.delete(code);
    else copy.add(code);
    onChangeSelection(copy);
  };

  const selectAll = () => onChangeSelection(new Set(subBasins.map((c) => c.subBasinCode)));
  const deselectAll = () => onChangeSelection(new Set());

  return (
    <div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selection.size === subBasins.length && subBasins.length > 0}
                  onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
                />
              </th>
              <th>{t("resultsPanel.subBasin")}</th>
              <th>{t("bulkAssigner.currentlyAssigned")}</th>
            </tr>
          </thead>
          <tbody>
            {subBasins.map((c) => (
              <tr
                key={c.subBasinCode}
                className={selection.has(c.subBasinCode) ? "row-selected" : undefined}
                onClick={() => toggle(c.subBasinCode)}
              >
                <td>
                  <input type="checkbox" checked={selection.has(c.subBasinCode)} readOnly />
                </td>
                <td>{c.description}</td>
                <td>{valueLabel(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="assigner-controls">
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">{t("common.choose")}</option>
          <option value={UNASSIGN_TOKEN}>{t("common.unassigned")}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          className="button"
          disabled={selection.size === 0 || value === ""}
          onClick={() => {
            if (value === UNASSIGN_TOKEN) onAssign(selection, unassignValue);
            else if (value !== "") onAssign(selection, Number(value));
            onChangeSelection(new Set());
          }}
        >
          {t("bulkAssigner.buttonWithCount", { text: buttonText, n: selection.size })}
        </button>
      </div>
    </div>
  );
}
